/**
 * Food record normalizer — the single validation contract for the nutrition
 * food catalog.
 *
 * Every food that reaches Firestore goes through `normalizeFood`, whoever
 * writes it:
 *   - `functions/foodSeedHelper.js` (the NiV "seed the catalog" button)
 *   - `scripts/importFoods.js`      (the same seed, from a terminal)
 *   - the "add custom food" form    (clinic `customFoods`)
 *   - the NiV promotion step        (clinic food -> global `foods`)
 *
 * so all three agree on what a valid food is. A record missing any of the four
 * mandatory macro fields is REJECTED, never imported with zeros: a food with
 * unknown calories silently poisons every menu total that uses it.
 *
 * NOTE ON MODULE FORMAT: `.mjs`, unlike the rest of `src/`. It is the only
 * module shared between the React app and a plain `node` maintenance script,
 * and `.mjs` is the one extension both can load: webpack/jest resolve it by its
 * explicit path, and node parses it as ESM even though the package is CommonJS.
 * Keep it dependency-free and side-effect-free.
 *
 * Units: all macro/micro values are PER 100 GRAMS.
 */

// ============================================================================
// Field contract
// ============================================================================

/** The four fields without which a food is worthless. */
export const REQUIRED_FIELDS = ["foodName", "calories", "protein", "fat", "carbohydrates"];

/** kcal per 100g above which we assume the row is garbage (pure fat is 900). */
export const MAX_CALORIES = 950;

/** Grams of protein+fat+carbs per 100g above which the row is impossible. */
export const MAX_MACRO_SUM = 105;

/** A single serving unit can never sensibly weigh more than this many grams. */
export const MAX_UNIT_GRAMS = 2000;

/**
 * Optional numeric fields we keep, in display order.
 * Anything not listed here is dropped — the import is allowlist-only, which is
 * what silently disposes of doof's `"\n"` key and other exporter noise.
 */
export const OPTIONAL_NUMERIC_FIELDS = [
  // macro detail
  "saturatedFat",
  "sugar",
  "dietaryFiber",
  "sodium",
  "cholesterol",
  "water",
  // minerals
  "calcium",
  "iron",
  "potassium",
  "magnesium",
  "phosphorus",
  "zinc",
  // vitamins
  "vitaminA",
  "vitaminC",
  "vitaminD",
  "vitaminE",
  "vitaminK",
  "vitaminB1",
  "vitaminB2",
  "vitaminB3",
  "vitaminB5",
  "vitaminB6",
  "vitaminB9",
  "vitaminB12",
  // fatty acids
  "omega3",
  "omega6",
  "monounsaturatedFat",
  "polyunsaturatedFat",
  "oleicAcid",
];

/**
 * doof key -> our key. Only keys that need renaming appear here.
 *
 * Deliberately NOT mapped, and therefore dropped:
 *   `vitaminB`   — a B-complex sum, not a vitamin; keeping it would render as
 *                  a bogus "ויטמין B" row beside B1..B12.
 *   `lycopene`, `selenium`, `copper`, `choline`, `B8` — present on 1-11 rows
 *                  out of 390; not worth a column in the UI.
 *   `sugarTeaspoons` — derivable as `sugar / 4`.
 *   `id`, `foodSource`, `dateAdded`, `lastModified` — exporter bookkeeping.
 */
const FIELD_ALIASES = {
  dietary_fiber: "dietaryFiber",
  D: "vitaminD",
  K: "vitaminK",
  B12: "vitaminB12",
  B: "vitaminB12",
};

/**
 * Serving units a coach may see. Curated by hand from the 99 distinct unit
 * names in the doof export: everything real is kept (including one-offs like
 * "כף גדושה"), and the old admin UI's keyboard-mash entries ("גדכדגכ",
 * "שולולולו", "כפול כפפית"...) are not.
 *
 * A unit outside this list is dropped from `sizeOptions` and reported, rather
 * than rejecting the whole food — the macro values are still good.
 */
export const CURATED_UNITS = new Set([
  // spoons & cups
  "כף",
  "כף גדושה",
  "כף שטוחה",
  "כף מדידה",
  "כף-קצוץ",
  "כף-טחון",
  "כפית",
  "כפית גדושה",
  "כפית שטוחה",
  "כפית-קצוץ",
  "כפית-טחון",
  "כוס",
  "כוס קוביות",
  "כוס-קצוץ",
  "כוס-טחון",
  "חופן",
  "סקופ",
  "קוביה",
  // whole units
  "יחידה",
  "יחידה קטנה",
  "יחידה בינונית",
  "יחידה גדולה",
  "יחידה ענקית",
  "חצי יחידה",
  "יחידה ללא קליפה",
  "חצי יחידה ללא קליפה",
  "יחידה קטנה ללא קליפה",
  "יחידה בינונית ללא קליפה",
  "יחידה גדולה ללא קליפה",
  "רבע יחידה קטנה",
  "רבע יחידה בינונית",
  "רבע יחידה גדולה",
  "7 יחידות",
  "גודל ממוצע",
  "גודל ממוצע - חלקים אכילים",
  // servings
  "מנה",
  "מנה קטנה",
  "מנה בינונית",
  "מנה גדולה",
  "מנת הגשה מומלצת",
  "פרוסה",
  "פלח",
  "פלח בינוני ללא קליפה",
  "פלח ללא קליפה",
  "פילה",
  "פס",
  "אונה",
  "לב",
  "ראש",
  "קלח",
  "סטייק קטן",
  "סטייק בינוני",
  "סטייק גדול",
  "סטייק ענק",
  // produce parts
  "עלה קטן",
  "עלה בינוני",
  "עלה גדול",
  "גבעול קטן",
  "גבעול בינוני",
  "גבעול גדול",
  "שורש קטן",
  "שורש בינוני",
  "שורש גדול",
  "אשכול",
  "אשכול קטן",
  "אשכול בינוני",
  "אשכול גדול",
  "צרור",
  "שרי תמר",
  // packaging
  "אריזת ניילון",
  "אריזת נייר",
  "אריזת ואקום",
  "חבילה",
  "שקית",
  "גביע",
  "קופסת פלסטיק",
  "קופסת קרטון",
  "קופסת פח",
  "קופסת שימורים",
  "צנצנת זכוכית",
  "צנצנת פלסטיק",
  "בקבוק זכוכית",
  "בקבוק פלסטיק",
  "בקבוק לחיץ",
  "בקבוקון פלסטיק",
]);

/**
 * `sizeOptions` keys that are UI placeholders in doof, not units:
 * "100 גרם" (359 rows) and "★ מותאם אישית" (384 rows) carry `""` / `null`.
 * They are dropped silently — they are noise on almost every record.
 */
const UNIT_SENTINELS = new Set([
  "100 גרם",
  "100 גרם לפני בישול",
  '100 מ"ל',
  "★ מותאם אישית",
]);

/** Names that identify leftover QA rows in the doof export. */
const JUNK_NAME_PATTERNS = [/בדיקה/, /^ניב\s/, /^טסט/, /^test/i];

// ============================================================================
// Primitives
// ============================================================================

/** Collapse whitespace and trim — used for display names and for matching. */
export const cleanText = (value) =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";

/**
 * Coerce to a finite number.
 *
 * Handles the two shapes doof stored numbers in: real numbers, and strings —
 * either plain ("240") or free text ("פחות מ-2.5", "פחות מ-0.1"), where we take
 * the number that is actually in there. Returns null when there is no number.
 */
export const toFiniteNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const direct = Number(trimmed);
  if (Number.isFinite(direct)) return direct;

  // Free text such as "פחות מ-2.5" -> 2.5. The sign is deliberately NOT part
  // of the pattern: the hyphen in "מ-2.5" is Hebrew punctuation, not a minus.
  const match = trimmed.match(/\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The lookup key for food search: lowercased, punctuation-free, single-spaced.
 * Stored on the doc so Firestore can range-query it (`>=` / `<= `).
 */
export const buildSearchName = (name) =>
  cleanText(name)
    .toLowerCase()
    .replace(/["'’״׳().,_/\\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Deterministic document id derived from the name, so re-running the import
 * updates the same docs instead of duplicating the catalog.
 * Firestore ids may not contain "/" and may not be "." or "..".
 */
export const buildFoodId = (name) => {
  const slug = buildSearchName(name).replace(/\s/g, "-").replace(/-+/g, "-");
  return slug || null;
};

export const isJunkName = (name) => JUNK_NAME_PATTERNS.some((re) => re.test(name));

// ============================================================================
// sizeOptions
// ============================================================================

/**
 * Normalize a doof `sizeOptions` map to `{ unit: grams }`.
 * Returns the cleaned map plus every dropped key with a reason, so the import
 * report can show exactly what was thrown away.
 */
export const normalizeSizeOptions = (raw) => {
  const sizeOptions = {};
  const dropped = [];

  if (!raw || typeof raw !== "object") return { sizeOptions, dropped };

  Object.entries(raw).forEach(([rawUnit, rawGrams]) => {
    const unit = cleanText(rawUnit);
    if (!unit) return;
    if (UNIT_SENTINELS.has(unit)) return; // UI placeholder, not a unit

    if (!CURATED_UNITS.has(unit)) {
      dropped.push({ unit, value: rawGrams, reason: "unknown-unit" });
      return;
    }

    const grams = toFiniteNumber(rawGrams);
    if (grams === null) {
      dropped.push({ unit, value: rawGrams, reason: "non-numeric" });
      return;
    }
    if (grams <= 0 || grams > MAX_UNIT_GRAMS) {
      dropped.push({ unit, value: rawGrams, reason: "out-of-range" });
      return;
    }

    sizeOptions[unit] = grams;
  });

  return { sizeOptions, dropped };
};

// ============================================================================
// normalizeFood
// ============================================================================

/**
 * Validate and normalize one raw food record.
 *
 * @param {object} raw - a doof export row, a custom-food form payload, or a
 *   clinic food being promoted. Extra keys are ignored (allowlist-only).
 * @param {object} [options]
 * @param {"global"|"clinic"} [options.source="global"] - written to the doc.
 * @param {string} [options.clinicId] - required when source is "clinic".
 * @returns {{ ok: boolean, food?: object, id?: string, errors: string[],
 *             warnings: Array<{field: string, reason: string, value: any}> }}
 */
export const normalizeFood = (raw, options = {}) => {
  const { source = "global", clinicId = null } = options;
  const errors = [];
  const warnings = [];

  if (!raw || typeof raw !== "object") {
    return { ok: false, errors: ["record is not an object"], warnings };
  }

  // --- name ----------------------------------------------------------------
  const foodName = cleanText(raw.foodName ?? raw.name);
  if (!foodName) {
    errors.push("foodName is missing or empty");
  } else if (isJunkName(foodName)) {
    errors.push(`foodName looks like test data: "${foodName}"`);
  }

  // --- mandatory macros ----------------------------------------------------
  const calories = toFiniteNumber(raw.calories);
  if (calories === null) {
    errors.push("calories is missing or not a number");
  } else if (calories < 0 || calories > MAX_CALORIES) {
    errors.push(`calories out of range (${calories}, expected 0-${MAX_CALORIES})`);
  }

  const macros = {};
  ["protein", "fat", "carbohydrates"].forEach((field) => {
    const value = toFiniteNumber(raw[field]);
    if (value === null) {
      errors.push(`${field} is missing or not a number`);
      return;
    }
    if (value < 0) {
      errors.push(`${field} is negative (${value})`);
      return;
    }
    macros[field] = value;
  });

  // A row claiming more than 100g of macros inside 100g of food is impossible.
  const macroSum =
    (macros.protein ?? 0) + (macros.fat ?? 0) + (macros.carbohydrates ?? 0);
  if (macroSum > MAX_MACRO_SUM) {
    errors.push(
      `macro sum is impossible (${macroSum.toFixed(1)}g per 100g, max ${MAX_MACRO_SUM})`
    );
  }

  if (source === "clinic" && !clinicId) {
    errors.push("clinicId is required for a clinic food");
  }

  if (errors.length > 0) {
    return { ok: false, errors, warnings, foodName };
  }

  // --- sizeOptions ---------------------------------------------------------
  const { sizeOptions, dropped } = normalizeSizeOptions(raw.sizeOptions);
  dropped.forEach((entry) =>
    warnings.push({
      field: "sizeOptions",
      reason: entry.reason,
      value: `${entry.unit}: ${JSON.stringify(entry.value)}`,
    })
  );

  // --- optional nutrients --------------------------------------------------
  // Resolve aliases first so `dietary_fiber`/`D`/`K`/`B12` land on our keys
  // without overwriting a value that already came in under the canonical name.
  const aliased = { ...raw };
  Object.entries(FIELD_ALIASES).forEach(([from, to]) => {
    if (aliased[from] === undefined) return;
    if (aliased[to] === undefined) aliased[to] = aliased[from];
    delete aliased[from];
  });

  const food = {
    foodName,
    searchName: buildSearchName(foodName),
    calories,
    protein: macros.protein,
    fat: macros.fat,
    carbohydrates: macros.carbohydrates,
    sizeOptions,
    source,
  };

  const sourceName = cleanText(aliased.sourceName ?? aliased.sourceFoodName);
  if (sourceName && sourceName !== foodName) food.sourceName = sourceName;

  OPTIONAL_NUMERIC_FIELDS.forEach((field) => {
    if (aliased[field] === undefined || aliased[field] === null) return;
    const value = toFiniteNumber(aliased[field]);
    if (value === null) {
      warnings.push({ field, reason: "non-numeric", value: aliased[field] });
      return;
    }
    if (value < 0) {
      warnings.push({ field, reason: "negative", value: aliased[field] });
      return;
    }
    food[field] = value;
  });

  const categoryId = cleanText(aliased.categoryId);
  if (categoryId) food.categoryId = categoryId;

  if (source === "clinic") food.clinicId = clinicId;

  return { ok: true, food, id: buildFoodId(foodName), errors, warnings };
};

/**
 * Pick the better of two records for the same food name.
 *
 * The doof export has 58 duplicated names (74 extra rows), usually the same
 * food scraped twice with different micronutrient coverage. We keep the richer
 * record, and break a tie with the newest `lastModified`.
 */
export const pickRicherFood = (a, b) => {
  const richness = (food) => Object.keys(food?.food || food || {}).length;
  const scoreA = richness(a);
  const scoreB = richness(b);
  if (scoreA !== scoreB) return scoreA > scoreB ? a : b;

  const timeOf = (candidate) => {
    const value = candidate?.lastModified;
    const ms = value ? Date.parse(value) : NaN;
    return Number.isFinite(ms) ? ms : 0;
  };
  return timeOf(b) > timeOf(a) ? b : a;
};

// ============================================================================
// Whole-export pass
// ============================================================================

/** More than this share of the input rejected means the export changed shape. */
export const MAX_REJECT_RATIO = 0.1;

/**
 * Normalize a whole export: reject what cannot be saved, merge duplicate names,
 * and report everything a human needs to decide whether to commit the import.
 *
 * Pure — no Firestore, no filesystem — so the Cloud Function behind the NiV
 * "seed" button and `scripts/importFoods.js` produce byte-identical results.
 *
 * @param {Array<Object>} records raw rows from the export
 * @returns {{docs: Array<{id, food, lastModified}>, rejected: Array, warnings: Array, duplicates: Array, counts: Object}}
 */
export const buildImport = (records) => {
  const rejected = [];
  const warnings = [];
  const duplicates = [];
  const byId = new Map();
  const list = Array.isArray(records) ? records : [];

  list.forEach((raw, index) => {
    const result = normalizeFood(raw, { source: "global" });

    if (!result.ok) {
      rejected.push({
        index,
        foodName: cleanText(raw?.foodName) || "(no name)",
        errors: result.errors,
        // Kept so near-misses (a beef row missing only `carbohydrates`) can be
        // fixed by hand and re-imported rather than lost.
        raw,
      });
      return;
    }

    if (result.warnings.length > 0) {
      warnings.push({ foodName: result.food.foodName, warnings: result.warnings });
    }

    const candidate = {
      id: result.id,
      food: result.food,
      lastModified: raw?.lastModified || raw?.dateAdded || null,
    };

    const existing = byId.get(result.id);
    if (!existing) {
      byId.set(result.id, candidate);
      return;
    }

    const winner = pickRicherFood(existing, candidate);
    byId.set(result.id, winner);
    duplicates.push({
      id: result.id,
      foodName: result.food.foodName,
      keptFields: Object.keys(winner.food).length,
      droppedFields: Object.keys((winner === existing ? candidate : existing).food).length,
    });
  });

  const docs = Array.from(byId.values());
  const rejectRatio = list.length ? rejected.length / list.length : 0;

  return {
    docs,
    rejected,
    warnings,
    duplicates,
    counts: {
      input: list.length,
      accepted: docs.length,
      rejected: rejected.length,
      duplicatesMerged: duplicates.length,
      recordsWithWarnings: warnings.length,
      rejectRatio: Number(rejectRatio.toFixed(4)),
      exceedsRejectLimit: rejectRatio > MAX_REJECT_RATIO,
    },
  };
};
